import { forwardRef, type ReactNode, useCallback } from "react";

import { trpc } from "@blis/trpc/react";

import { RoleCondition } from "@/components/RoleCondition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label, TextArea, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import type { THold } from "@/lib/types";
import { format } from "@/lib/utils";

export const HoldDetailDialog = forwardRef<
  HTMLButtonElement,
  {
    hold: THold;
    children: ReactNode;
  }
>(({ children, hold }, _ref) => {
  const resolveMutation = trpc.holds.resolve.useMutation({
    onSuccess() {
      toast.success("Hold resolved");
    },
    onError(err) {
      toast.error("Error resolving hold", {
        description: err.message,
      });
    },
  });

  const cancelMutation = trpc.holds.cancel.useMutation({
    onSuccess() {
      toast.success("Hold cancelled");
    },
    onError(err) {
      toast.error("Error cancelling hold", {
        description: err.message,
      });
    },
  });

  const onCancel = useCallback(
    (value: string) => {
      cancelMutation.mutate({ id: hold.id, comment: value });
    },
    [cancelMutation, hold.id],
  );

  const onResolve = useCallback(
    (value: string) => {
      resolveMutation.mutate({ id: hold.id, comment: value });
    },
    [hold.id, resolveMutation],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const values = Object.fromEntries(new FormData(e.currentTarget));
      const name = (e.nativeEvent as SubmitEvent).submitter?.getAttribute(
        "name",
      );

      if (name === "resolve") {
        onResolve(values.comment as string);
      } else if (name === "cancel") {
        onCancel(values.comment as string);
      }
    },
    [onResolve, onCancel],
  );

  return (
    <DialogTrigger>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Hold details</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-4">
              <HoldDetailItem title="Status">
                {hold.resolved && !hold.canceled ? (
                  <Badge color="green">Resolved</Badge>
                ) : hold.canceled ? (
                  <Badge color="gray">Cancelled</Badge>
                ) : (
                  <Badge color="red">Open</Badge>
                )}
              </HoldDetailItem>

              <HoldDetailItem
                title="Description"
                caption={`Created by ${hold.createdBy.username} on ${format(hold.createdAt)}`}
              >
                {hold.description}
              </HoldDetailItem>

              <RoleCondition
                roles={[
                  "blis_lab_manager",
                  "blis_lab_tech",
                  "blis_lab_assistant",
                ]}
              >
                {!hold.resolved && !hold.canceled && (
                  <Form
                    onSubmit={onSubmit}
                    className="mt-4"
                    id="hold-action-form"
                  >
                    <TextField name="comment" isRequired>
                      <Label>Comment</Label>
                      <TextArea placeholder="Add a comment" />
                    </TextField>
                  </Form>
                )}
              </RoleCondition>

              {hold.resolved && !hold.canceled && (
                <HoldDetailItem
                  title="Resolution"
                  caption={`Resolved by ${hold.resolvedBy?.username || "-"} on ${format(hold.resolvedAt)}`}
                >
                  {hold.resolvedComment}
                </HoldDetailItem>
              )}
              {hold.canceled && (
                <HoldDetailItem
                  title="Cancelled"
                  caption={`Cancelled by ${hold.cancelledBy?.username || "-"} on ${format(hold.canceledAt)}`}
                >
                  {hold.canceledComment}
                </HoldDetailItem>
              )}
              {!hold.canceled && !hold.resolved && (
                <RoleCondition
                  roles={[
                    "blis_lab_manager",
                    "blis_lab_tech",
                    "blis_lab_assistant",
                  ]}
                >
                  <div className="flex items-center gap-2">
                    {/* <HoldCancelButton hold={hold} /> */}
                    {/* <HoldResolveButton hold={hold} /> */}
                  </div>
                </RoleCondition>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <RoleCondition
              roles={[
                "blis_lab_manager",
                "blis_lab_tech",
                "blis_lab_assistant",
              ]}
            >
              {!hold.resolved && !hold.canceled && (
                <>
                  <Button
                    type="submit"
                    name="resolve"
                    form="hold-action-form"
                    isPending={resolveMutation.isPending}
                    isDisabled={resolveMutation.isPending}
                  >
                    Resolve hold
                  </Button>
                  <Button
                    variant="outline"
                    type="submit"
                    name="cancel"
                    form="hold-action-form"
                    isPending={cancelMutation.isPending}
                    isDisabled={cancelMutation.isPending}
                  >
                    Cancel hold
                  </Button>
                </>
              )}
            </RoleCondition>
            <DialogCloseButton>Close</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
});

function HoldDetailItem({
  caption,
  title,
  children,
}: {
  title: string;
  caption?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <Label className="block pb-0.5">{title}</Label>
      {typeof children === "string" ? (
        <Text elementType="p" className="text-pretty text-muted-foreground">
          {children}
        </Text>
      ) : (
        children
      )}
      {caption && (
        <Text muted size="1">
          {caption}
        </Text>
      )}
    </div>
  );
}
